/**
 * NoteCache - Centralized note storage and subscription system
 *
 * This cache serves as the single source of truth for Note objects in memory,
 * providing:
 * - Optimistic updates with immediate UI feedback
 * - Selective subscriptions to avoid unnecessary re-renders
 * - Suggested notes support (cache-only, never persisted)
 * - Efficient batch operations and change notifications
 */
import { storageLogger } from '@annote/core/utils/debug';
import { Note, UUID } from '@annote/types';
import { annoteIndex } from './annoteIndex';

export type NoteChangeListener = (changedIds: string[]) => void;
export type NoteSelector<T> = (note: Note | undefined) => T;

export interface UpsertOptions {
  silentFields?: string[]; // Fields to ignore for change detection
  silentUpdate?: boolean; // Don't notify subscribers
}

export interface NoteCache {
  // Core cache operations
  get(id: UUID): Note | undefined;
  getMany(ids: UUID[]): Note[]; // Get multiple notes by IDs
  upsert(note: Note, opts?: UpsertOptions): UUID[]; // returns changed ids
  upsertMany(notes: Note[], opts?: UpsertOptions): UUID[];
  has(id: UUID): boolean;

  // Subscription management
  subscribe(listener: NoteChangeListener): () => void; // returns unsubscribe

  // Suggested notes (cache-only)
  addSuggestedId(id: UUID): void;
  removeSuggestedNote(id: UUID): void; // Remove suggested note from cache
  getSuggestedIds(): UUID[];
  clearSuggestions(): void; // Clear all suggested notes

  // Note removal
  remove(id: UUID): void; // Remove note from cache entirely
  removeMany(ids: UUID[]): void; // Remove multiple notes from cache

  // Utility methods
  getRecentlyUpdatedIds(limit?: number): UUID[];
}

class NoteCacheImpl implements NoteCache {
  private notes: Map<UUID, Note> = new Map();

  private listeners: Set<NoteChangeListener> = new Set();

  private suggestedIds: Set<UUID> = new Set();

  get(id: UUID): Note | undefined {
    return this.notes.get(id);
  }

  has(id: UUID): boolean {
    return this.notes.has(id);
  }

  getMany(ids: UUID[]): Note[] {
    return ids.map((id) => this.notes.get(id)).filter((note): note is Note => note !== undefined);
  }

  upsert(note: Note, opts: UpsertOptions = {}): UUID[] {
    return this.upsertMany([note], opts);
  }

  upsertMany(notes: Note[], opts: UpsertOptions = {}): UUID[] {
    const changedIds: UUID[] = [];
    const { silentFields = [], silentUpdate = false } = opts;

    notes.forEach((note) => {
      const existingNote = this.notes.get(note.id);
      let hasChanged = false;

      if (!existingNote) {
        // New note
        hasChanged = true;
      } else {
        // Check if note has actually changed
        hasChanged = NoteCacheImpl.hasNoteChanged(existingNote, note, silentFields);
      }

      if (hasChanged) {
        this.notes.set(note.id, { ...note });
        changedIds.push(note.id);

        // Track suggested notes (by presence of suggestionId)
        if (note.suggestionId) {
          this.suggestedIds.add(note.id);
        } else if (this.suggestedIds.has(note.id) && !note.suggestionId) {
          // Remove from suggested if suggestionId was removed
          this.suggestedIds.delete(note.id);
        }

        // Update annote index
        annoteIndex.upsertNote(note);
      }
    });

    // Notify subscribers if there are changes and it's not silent
    if (changedIds.length > 0 && !silentUpdate) {
      this.notifyChange(changedIds);
    }

    return changedIds;
  }

  private static hasNoteChanged(existingNote: Note, newNote: Note, silentFields: string[]): boolean {
    // Quick reference check
    if (existingNote === newNote) return false;

    // Check each field for changes, excluding silent fields
    const allFields = new Set([...Object.keys(existingNote), ...Object.keys(newNote)]);
    const fieldsToCheck = Array.from(allFields).filter((field) => !silentFields.includes(field));

    // eslint-disable-next-line no-restricted-syntax
    for (const field of fieldsToCheck) {
      const existingValue = (existingNote as Record<string, unknown>)[field];
      const newValue = (newNote as Record<string, unknown>)[field];

      // Handle undefined vs missing property
      if (existingValue !== newValue) {
        if (Array.isArray(existingValue) && Array.isArray(newValue)) {
          // Array comparison
          if (existingValue.length !== newValue.length || !existingValue.every((val, idx) => val === newValue[idx])) {
            return true;
          }
        } else {
          return true;
        }
      }
    }

    return false;
  }

  subscribe(listener: NoteChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyChange(changedIds: UUID[]): void {
    Array.from(this.listeners).forEach((listener) => {
      try {
        listener(changedIds);
      } catch (error) {
        storageLogger.error('Error in NoteCache listener:', error);
      }
    });
  }

  // Suggested notes management
  addSuggestedId(id: UUID): void {
    this.suggestedIds.add(id);
  }

  removeSuggestedNote(id: UUID): void {
    this.suggestedIds.delete(id);
    this.notes.delete(id); // Remove from cache entirely
  }

  getSuggestedIds(): UUID[] {
    return Array.from(this.suggestedIds);
  }

  clearSuggestions(): void {
    const clearedIds = Array.from(this.suggestedIds);
    clearedIds.forEach((id) => {
      this.notes.delete(id); // Remove from cache entirely
    });
    this.suggestedIds.clear();
    if (clearedIds.length > 0) {
      this.notifyChange(clearedIds);
    }
  }

  remove(id: UUID): void {
    if (this.notes.has(id)) {
      this.notes.delete(id);
      this.suggestedIds.delete(id);
      annoteIndex.removeNote(id);
      this.notifyChange([id]);
    }
  }

  removeMany(ids: UUID[]): void {
    const removedIds: UUID[] = [];
    ids.forEach((id) => {
      if (this.notes.has(id)) {
        this.notes.delete(id);
        this.suggestedIds.delete(id);
        annoteIndex.removeNote(id);
        removedIds.push(id);
      }
    });
    if (removedIds.length > 0) {
      this.notifyChange(removedIds);
    }
  }

  // Debugging/internal methods
  size(): number {
    return this.notes.size;
  }

  // Fix sorting for getRecentlyUpdatedIds - prioritize optimistic updates (null syncedAt)
  getRecentlyUpdatedIds(limit: number = 10): UUID[] {
    const oldTime = new Date('1970-01-01').toISOString();

    // Get all notes that are not suggestions, sort by syncedAt (null values first)
    const allNotes = Array.from(this.notes.values())
      .filter((note) => !note.suggestionId)
      .sort((a, b) => {
        const aTime = a.syncedAt || oldTime;
        const bTime = b.syncedAt || oldTime;
        // Descending order (newest first, nulls first)
        return bTime.localeCompare(aTime);
      })
      .slice(0, limit);

    return allNotes.map((note) => note.id);
  }

  clear(): void {
    this.notes.clear();
    this.suggestedIds.clear();
    this.listeners.clear();
  }
}

// Export singleton instance
export const noteCache: NoteCache = new NoteCacheImpl();

// For testing purposes
export const createNoteCache = (): NoteCache => new NoteCacheImpl();
